import React, { useEffect, useState } from "react";
import { Table, Button, Form, Row, Col, Spinner, Alert } from "react-bootstrap";
import Swal from "sweetalert2";
import axios from "axios";
import API_BASE_URL from "../config";

export default function BusinessHoursPage() {
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const storedCompany = localStorage.getItem("selectedCompany");
    return storedCompany ? JSON.parse(storedCompany) : null;
  });
  const [selectedBranch, setSelectedBranch] = useState(() => {
    const storedBranch = localStorage.getItem("selectedBranch");
    return storedBranch ? JSON.parse(storedBranch).id : "all";
  });

  const [hours, setHours] = useState([]);
  const [applyAllOpen, setApplyAllOpen] = useState("");
  const [applyAllClose, setApplyAllClose] = useState("");

  const [closures, setClosures] = useState([]);
  const [closureForm, setClosureForm] = useState({
    start_date: "",
    end_date: "",
    reason: "",
    is_full_close: false,
  });
  const [loading, setLoading] = useState(false);

  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  // 🔹 Fetch branches for a company
  const fetchBranches = async (companyId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/companies/${companyId}/branches`);
      const branchData = res.data || [];
      setBranches(branchData);

      // Validate stored selectedBranch
      const storedBranch = localStorage.getItem("selectedBranch");
      if (storedBranch) {
        const parsedBranch = JSON.parse(storedBranch);
        const isValidBranch = branchData.some((b) => b.id === parsedBranch.id);
        if (!isValidBranch) {
          localStorage.removeItem("selectedBranch");
          setSelectedBranch("all");
        } else {
          setSelectedBranch(parsedBranch.id);
        }
      }
    } catch (err) {
      console.error("fetchBranches", err);
      Swal.fire("Error", "Failed to load branches", "error");
      setSelectedBranch("all");
    }
  };

  // 🔹 Load companies and validate selectedCompany
  useEffect(() => {
    async function fetchCompanies() {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const companyData = res.data || [];
        setCompanies(companyData);

        // Validate stored selectedCompany
        const storedCompany = localStorage.getItem("selectedCompany");
        if (storedCompany) {
          const parsedCompany = JSON.parse(storedCompany);
          const isValidCompany = companyData.some((c) => c.id === parsedCompany.id);
          if (!isValidCompany) {
            localStorage.removeItem("selectedCompany");
            localStorage.removeItem("selectedBranch");
            setSelectedCompany(null);
            setSelectedBranch("all");
          } else {
            setSelectedCompany(parsedCompany);
            fetchBranches(parsedCompany.id);
          }
        }
      } catch (err) {
        console.error("fetchCompanies", err);
        Swal.fire("Error", "Failed to load companies", "error");
        setSelectedCompany(null);
        setSelectedBranch("all");
      }
    }
    fetchCompanies();
  }, []);

  // 🔹 Load branches when selectedCompany changes
  useEffect(() => {
    if (!selectedCompany) {
      setBranches([]);
      setHours([]);
      setClosures([]);
      setSelectedBranch("all");
      return;
    }

    fetchBranches(selectedCompany.id);
  }, [selectedCompany]);

  // 🔹 Fetch hours and closures when selectedCompany or selectedBranch changes
  useEffect(() => {
    if (selectedCompany) fetchData();
  }, [selectedCompany, selectedBranch]);

  const fetchData = async () => {
    if (!selectedCompany) {
      setHours([]);
      setClosures([]);
      return;
    }
    setLoading(true);
    try {
      const url =
        selectedBranch === "all"
          ? `${API_BASE_URL}/companies/${selectedCompany.id}/hours`
          : `${API_BASE_URL}/branches/${selectedBranch}/hours`;
      const closuresUrl = `${API_BASE_URL}/companies/${selectedCompany.id}/closures`;

      const [hoursRes, closuresRes] = await Promise.all([
        axios.get(url),
        axios.get(closuresUrl),
      ]);

      const hoursData = Array.isArray(hoursRes.data) ? hoursRes.data : [];
      setHours(hoursData);

      const firstDay = hoursData.find((h) => h.is_closed === 0);
      setApplyAllOpen(firstDay?.open_time || "");
      setApplyAllClose(firstDay?.close_time || "");

      setClosures(closuresRes.data || []);
    } catch (err) {
      console.error("fetchData", err);
      Swal.fire("Error", "Failed to load hours or closures", "error");
      setHours([]);
      setClosures([]);
    } finally {
      setLoading(false);
    }
  };

  const saveHours = async () => {
    if (!selectedCompany) {
      Swal.fire("Validation", "Select a company first", "warning");
      return;
    }
    const url =
      selectedBranch === "all"
        ? `${API_BASE_URL}/companies/${selectedCompany.id}/hours`
        : `${API_BASE_URL}/branches/${selectedBranch}/hours`;
    try {
      await axios.post(url, hours);
      Swal.fire("Saved!", "Working hours updated.", "success");
    } catch (err) {
      console.error("saveHours", err);
      Swal.fire("Error", "Failed to save hours", "error");
    }
  };

  const saveClosure = async () => {
    if (!selectedCompany) {
      Swal.fire("Validation", "Select a company first", "warning");
      return;
    }
    if (!closureForm.start_date) {
      Swal.fire("Validation", "Please select start date", "warning");
      return;
    }
    if (!closureForm.is_full_close && !closureForm.end_date) {
      Swal.fire("Validation", "Please select end date (or check Full Close for indefinite)", "warning");
      return;
    }
    try {
      await axios.post(
        `${API_BASE_URL}/companies/${selectedCompany.id}/closures`,
        {
          ...closureForm,
          end_date: closureForm.is_full_close ? null : closureForm.end_date,
          branch_id: selectedBranch === "all" ? null : selectedBranch,
        }
      );
      Swal.fire(
        "Added!",
        closureForm.is_full_close ? "🚨 FULL closure added!" : "Closure added",
        "success"
      );
      setClosureForm({ start_date: "", end_date: "", reason: "", is_full_close: false });
      fetchData();
    } catch (err) {
      console.error("saveClosure", err);
      Swal.fire("Error", "Failed to add closure", "error");
    }
  };

  const deleteClosure = async (id) => {
    const result = await Swal.fire({
      title: "Delete Closure?",
      text: "This cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${API_BASE_URL}/closures/${id}`);
      Swal.fire("Deleted!", "Closure removed.", "success");
      fetchData();
    } catch (err) {
      console.error("deleteClosure", err);
      Swal.fire("Error", "Failed to delete closure", "error");
    }
  };

  const closeAllCompany = async () => {
    if (!selectedCompany) {
      Swal.fire("Validation", "Select a company first", "warning");
      return;
    }
    const result = await Swal.fire({
      title: "🚨 Close ALL?",
      text: "This will close the COMPANY + ALL BRANCHES until manually resumed",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Close All",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.post(`${API_BASE_URL}/companies/${selectedCompany.id}/close-all`, {
        reason: "Company-wide closure",
        is_full_close: 1,
      });
      Swal.fire("Closed!", "Company + All Branches closed", "success");
      fetchData();
    } catch (err) {
      console.error("closeAllCompany", err);
      Swal.fire("Error", "Failed to close company", "error");
    }
  };

  const closeCurrentBranch = async () => {
    if (!selectedCompany) {
      Swal.fire("Validation", "Select a company first", "warning");
      return;
    }
    const branchName = selectedBranch === "all" ? "Company + All Branches" : branches.find((b) => b.id == selectedBranch)?.name;
    const result = await Swal.fire({
      title: "⚠️ Close Branch + Company?",
      text: `This will close ${branchName} until manually resumed`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Close",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#fd7e14",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.post(`${API_BASE_URL}/companies/${selectedCompany.id}/close-all`, {
        reason: "Branch closure triggered",
        branch_id: selectedBranch === "all" ? null : selectedBranch,
        is_full_close: 1,
      });
      Swal.fire("Closed!", "Company + Branch closed", "success");
      fetchData();
    } catch (err) {
      console.error("closeCurrentBranch", err);
      Swal.fire("Error", "Failed to close", "error");
    }
  };

  const resumeAllCompany = async () => {
    if (!selectedCompany) {
      Swal.fire("Validation", "Select a company first", "warning");
      return;
    }
    const result = await Swal.fire({
      title: "✅ Resume ALL?",
      text: "This will resume the COMPANY + ALL BRANCHES with default hours (09:00-18:00)",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Resume All",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#28a745",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.post(`${API_BASE_URL}/companies/${selectedCompany.id}/resume-all`, {
        branch_id: null,
      });
      Swal.fire("Resumed!", "Company + All Branches resumed", "success");
      fetchData();
    } catch (err) {
      console.error("resumeAllCompany", err);
      Swal.fire("Error", "Failed to resume company", "error");
    }
  };

  const resumeCurrentBranch = async () => {
    if (!selectedCompany) {
      Swal.fire("Validation", "Select a company first", "warning");
      return;
    }
    const branchName = selectedBranch === "all" ? "Company + All Branches" : branches.find((b) => b.id == selectedBranch)?.name;
    const result = await Swal.fire({
      title: "✅ Resume Branch + Company?",
      text: `This will resume ${branchName} with default hours (09:00-18:00)`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Resume",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#28a745",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.post(`${API_BASE_URL}/companies/${selectedCompany.id}/resume-all`, {
        branch_id: selectedBranch === "all" ? null : selectedBranch,
      });
      Swal.fire("Resumed!", "Company + Branch resumed", "success");
      fetchData();
    } catch (err) {
      console.error("resumeCurrentBranch", err);
      Swal.fire("Error", "Failed to resume", "error");
    }
  };

  const applyToAllDays = () => {
    if (!applyAllOpen || !applyAllClose) {
      Swal.fire("Warning", "Please select both open and close times.", "warning");
      return;
    }
    setHours((prev) => {
      const updated = days.map((day) => {
        const existing = prev.find((h) => h.day_of_week === day);
        return {
          ...existing,
          day_of_week: day,
          open_time: applyAllOpen,
          close_time: applyAllClose,
          is_closed: 0,
        };
      });
      return updated;
    });
    Swal.fire("Applied", "Working hours copied to all days.", "success");
  };

  const updateHour = (day, field, value) => {
    setHours((prev) => {
      const copy = [...prev];
      const idx = copy.findIndex((h) => h.day_of_week === day);
      if (idx >= 0) {
        copy[idx][field] = value;
      } else {
        copy.push({
          day_of_week: day,
          open_time: field === "open_time" ? value : "",
          close_time: field === "close_time" ? value : "",
          is_closed: 0,
        });
      }
      return copy;
    });
  };

  const hasFullClosure = closures.some((c) => c.is_full_close === 1);

  return (
    <div className="container-fluid mt-4">
      <Row className="justify-content-center">
        <Col xs={12} lg={10} xl={8}>
          <h3 className="mb-4 text-center">
            <i className="bi bi-clock-history me-2" />
            Business Hours & Closures
          </h3>

          <Row className="mb-3 g-2">
            <Col xs={6} md={4} lg={2}>
              <Button variant="danger" size="sm" className="w-100" onClick={closeAllCompany}>
                <i className="bi bi-exclamation-triangle me-1" /> Close All
              </Button>
            </Col>
            <Col xs={6} md={4} lg={2}>
              <Button variant="warning" size="sm" className="w-100" onClick={closeCurrentBranch}>
                <i className="bi bi-exclamation-circle me-1" /> Close Branch
              </Button>
            </Col>
            {hasFullClosure && (
              <>
                <Col xs={6} md={4} lg={2}>
                  <Button variant="success" size="sm" className="w-100" onClick={resumeAllCompany}>
                    <i className="bi bi-check-circle me-1" /> Resume All
                  </Button>
                </Col>
                <Col xs={6} md={4} lg={2}>
                  <Button variant="success" size="sm" className="w-100" onClick={resumeCurrentBranch}>
                    <i className="bi bi-check-circle me-1" /> Resume Branch
                  </Button>
                </Col>
              </>
            )}
          </Row>

          {hasFullClosure && (
            <Alert variant="danger" className="mb-4 text-center">
              <strong><i className="bi bi-exclamation-triangle-fill me-2" /> FULL CLOSURE ACTIVE!</strong> Company + branches closed until manually resumed
            </Alert>
          )}

          <Row className="mb-4 g-3">
            <Col xs={12} md={6} lg={4}>
              <Form.Group>
                <Form.Label>Company</Form.Label>
                <Form.Select
                  value={selectedCompany?.id || ""}
                  onChange={(e) => {
                    const company = companies.find((c) => c.id === Number(e.target.value));
                    setSelectedCompany(company);
                    setSelectedBranch("all");
                    localStorage.setItem("selectedCompany", JSON.stringify(company || {}));
                    localStorage.removeItem("selectedBranch");
                  }}
                  aria-label="Select Company"
                >
                  <option value="">Select Company</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={4}>
              <Form.Group>
                <Form.Label>Branch</Form.Label>
                <Form.Select
                  value={selectedBranch}
                  onChange={(e) => {
                    const branchId = e.target.value;
                    setSelectedBranch(branchId);
                    const selectedBranchObj = branches.find((b) => b.id === branchId) || {};
                    localStorage.setItem("selectedBranch", JSON.stringify(selectedBranchObj));
                  }}
                  aria-label="Select Branch"
                >
                  <option value="all">All Branches</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <Row className="align-items-end mb-3 g-2">
                <Col xs={12} sm={6} md={4} lg={3}>
                  <Form.Group>
                    <Form.Label>Open Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={applyAllOpen}
                      onChange={(e) => setApplyAllOpen(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6} md={4} lg={3}>
                  <Form.Group>
                    <Form.Label>Close Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={applyAllClose}
                      onChange={(e) => setApplyAllClose(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4} lg={3} className="mt-3 mt-md-0">
                  <Button variant="outline-primary" size="sm" className="w-100" onClick={applyToAllDays}>
                    Apply to All Days
                  </Button>
                </Col>
              </Row>

              <Table bordered hover responsive size="sm" className="mt-3">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Open</th>
                    <th>Close</th>
                    <th>Closed?</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((day) => {
                    const existing = hours.find((h) => h.day_of_week === day) || {};
                    return (
                      <tr key={day}>
                        <td>{day}</td>
                        <td>
                          <Form.Control
                            type="time"
                            value={existing.open_time || ""}
                            onChange={(e) => updateHour(day, "open_time", e.target.value)}
                            disabled={existing.is_closed === 1}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="time"
                            value={existing.close_time || ""}
                            onChange={(e) => updateHour(day, "close_time", e.target.value)}
                            disabled={existing.is_closed === 1}
                          />
                        </td>
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={existing.is_closed === 1}
                            onChange={(e) => updateHour(day, "is_closed", e.target.checked ? 1 : 0)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <div className="text-center mb-4">
                <Button onClick={saveHours} variant="success" size="sm">
                  Save Working Hours
                </Button>
              </div>

              <hr />
              <h5 className="text-center">Holidays / Closures</h5>
              <Row className="g-2 mb-3">
                <Col xs={12} sm={6} md={3}>
                  <Form.Control
                    type="date"
                    value={closureForm.start_date}
                    onChange={(e) => setClosureForm((f) => ({ ...f, start_date: e.target.value }))}
                  />
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <Form.Control
                    type="date"
                    value={closureForm.end_date}
                    onChange={(e) => setClosureForm((f) => ({ ...f, end_date: e.target.value }))}
                    disabled={closureForm.is_full_close}
                  />
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <Form.Control
                    placeholder="Reason (optional)"
                    value={closureForm.reason}
                    onChange={(e) => setClosureForm((f) => ({ ...f, reason: e.target.value }))}
                  />
                </Col>
                <Col xs={12} sm={6} md={2}>
                  <Form.Check
                    type="checkbox"
                    label="🚨 FULL CLOSE"
                    checked={closureForm.is_full_close}
                    onChange={(e) =>
                      setClosureForm((f) => ({
                        ...f,
                        is_full_close: e.target.checked,
                        end_date: e.target.checked ? "" : f.end_date,
                      }))
                    }
                  />
                </Col>
                <Col xs={12} sm={6} md={1}>
                  <Button variant="primary" size="sm" className="w-100" onClick={saveClosure}>
                    Add
                  </Button>
                </Col>
              </Row>

              <Table bordered responsive size="sm">
                <thead>
                  <tr>
                    <th>Start</th>
                    <th>End</th>
                    <th>Reason</th>
                    <th>Branch</th>
                    <th>🚨 Full?</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {closures.map((c) => (
                    <tr key={c.id} className={c.is_full_close ? "table-danger" : ""}>
                      <td>{c.start_date}</td>
                      <td>{c.end_date || "Indefinite"}</td>
                      <td>{c.reason || "-"}</td>
                      <td>
                        {c.branch_id ? branches.find((b) => b.id === c.branch_id)?.name || "-" : "All Branches"}
                      </td>
                      <td className="text-center">
                        {c.is_full_close ? <i className="bi bi-exclamation-triangle-fill text-danger" /> : ""}
                      </td>
                      <td>
                        <Button
                          variant="link"
                          className="text-danger p-1"
                          onClick={() => deleteClosure(c.id)}
                          title="Delete Closure"
                        >
                          <i className="bi bi-trash fs-5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
}